import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  deleteTemplate,
  duplicateTemplate,
  getTemplateById,
  getTemplateContentVersion,
  getTemplateContentVersions,
  getTemplates,
  insertTemplate,
  updateTemplate,
} from '../services/templates.service';
import type {
  CreateTemplateInput,
  GetTemplatesParams,
  Template,
  TemplateContentVersionDetail,
  TemplateContentVersionSummary,
  TemplatesPagingResult,
  UpdateTemplateInput,
} from '../types/templates.types';

const invalidateTemplates = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['templates'] });
  queryClient.invalidateQueries({ queryKey: ['template'] });
};

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

export const useGetTemplates = (
  params: GetTemplatesParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<TemplatesPagingResult>({
    queryKey: ['templates', params],
    queryFn: () => getTemplates(params),
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
  });
};

export const useSearchTemplates = (search: string, params: GetTemplatesParams = {}) => {
  const debouncedSearch = useDebouncedValue(search, 300);

  return useGetTemplates({
    ...params,
    q: debouncedSearch.trim() || undefined,
  });
};

export const useGetTemplate = (templateId: string, options: { enabled?: boolean } = {}) => {
  const id = templateId.trim();

  return useGlobalQuery<Template>({
    queryKey: ['template', id],
    queryFn: () => getTemplateById(id),
    staleTime: 30 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<Template, Error, CreateTemplateInput>({
    mutationFn: insertTemplate,
    suppressToast: true,
    onSuccess: () => {
      invalidateTemplates(queryClient);
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<Template, Error, { templateId: string; input: UpdateTemplateInput }>({
    mutationFn: ({ templateId, input }) => updateTemplate(templateId, input),
    suppressToast: true,
    onSuccess: (_result, variables) => {
      invalidateTemplates(queryClient);
      queryClient.invalidateQueries({ queryKey: ['template', variables.templateId] });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<{ deleted: boolean }, Error, string>({
    mutationFn: deleteTemplate,
    suppressToast: true,
    onSuccess: (_result, templateId) => {
      invalidateTemplates(queryClient);
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
    },
  });
};

export const useDuplicateTemplate = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<Template, Error, string>({
    mutationFn: duplicateTemplate,
    suppressToast: true,
    onSuccess: () => {
      invalidateTemplates(queryClient);
    },
  });
};

export const useGetTemplateContentVersions = (
  templateId: string,
  options: { enabled?: boolean } = {}
) => {
  const id = templateId.trim();

  return useGlobalQuery<TemplateContentVersionSummary[]>({
    queryKey: ['template', id, 'versions'],
    queryFn: () => getTemplateContentVersions(id),
    staleTime: 30 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id),
  });
};

export const useGetTemplateContentVersion = (
  templateId: string,
  version: number,
  options: { enabled?: boolean } = {}
) => {
  const id = templateId.trim();

  return useGlobalQuery<TemplateContentVersionDetail>({
    queryKey: ['template', id, 'versions', version],
    queryFn: () => getTemplateContentVersion(id, version),
    staleTime: 60 * 1000,
    enabled: (options.enabled ?? true) && Boolean(id) && Number.isFinite(version),
  });
};
